import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import UpgradeModal from '../UpgradeModal';
import Tooltip from '../Tooltip';
import { BACKEND_URL } from '../../config';
import { useOrganization, setCompletionInProgress, clearCompletionInProgress } from '../../context/OrganizationContext';
import { fetchWithOrg } from '../../utils/api';
import { usePlan } from '../../context/PlanContext';
import { useNavigate } from 'react-router-dom';
import { trackAssessmentStarted, trackAssessmentCompleted } from '../../lib/posthog';
import CreditCostEstimate from '../CreditCostEstimate';

import {
  ArrowLeft,
  ArrowRight,
  ChartBar,
  CheckCircle,
  CircleNotch,
  CurrencyDollar,
  FileImage,
  GearSix,
  Lightning,
  Lock,
  Question,
  Sparkle,
  Target,
  UploadSimple,
  UsersThree,
  X
} from '@phosphor-icons/react';
// ═══════════════════════════════════════════════════════════════════════════════
// INDUSTRY CATEGORIES - Now includes Trades & Small Business
// ═══════════════════════════════════════════════════════════════════════════════

const INDUSTRY_CATEGORIES = {
  // Enterprise/Corporate Industries
  corporate: [
    'E-commerce & Retail',
    'Professional Services',
    'Healthcare',
    'Finance & Insurance',
    'Manufacturing',
    'Technology & Software',
    'Education',
    'Real Estate',
    'Hospitality',
  ],
  // Trades & Construction
  trades: [
    'Plumbing',
    'Electrical',
    'HVAC & Air Conditioning',
    'Carpentry & Joinery',
    'Roofing',
    'Painting & Decorating',
    'Building & Construction',
    'Landscaping & Gardening',
  ],
  // Small Business & Services
  smallBusiness: [
    'Hairdressing & Barbershop',
    'Beauty & Aesthetics',
    'Cleaning Services',
    'Automotive & Mechanics',
    'Photography & Videography',
    'Café & Restaurant',
    'Fitness & Personal Training',
    'Pet Services & Grooming',
  ],
  // Health & Wellness (Small Practice)
  healthServices: [
    'Physiotherapy',
    'Dental Practice',
    'Chiropractic',
    'Veterinary',
    'Massage & Therapy',
    'Allied Health',
  ],
};

// Flatten for dropdown with category headers
const INDUSTRIES = [
  // Corporate
  ...INDUSTRY_CATEGORIES.corporate,
  // Separator handled in UI
  '── Trades & Construction ──',
  ...INDUSTRY_CATEGORIES.trades,
  '── Small Business & Services ──',
  ...INDUSTRY_CATEGORIES.smallBusiness,
  '── Health & Wellness ──',
  ...INDUSTRY_CATEGORIES.healthServices,
  // Other
  'Other'
];

// Industries that trigger small business flow
const SMALL_BUSINESS_INDUSTRIES = [
  ...INDUSTRY_CATEGORIES.trades,
  ...INDUSTRY_CATEGORIES.smallBusiness,
  ...INDUSTRY_CATEGORIES.healthServices,
];

// Helper to check if industry is small business/trade
const isSmallBusinessIndustry = (industry) => {
  return SMALL_BUSINESS_INDUSTRIES.includes(industry);
};

// ═══════════════════════════════════════════════════════════════════════════════
// DEPARTMENTS vs BUSINESS FUNCTIONS (Adaptive)
// ═══════════════════════════════════════════════════════════════════════════════

// Traditional corporate departments
const DEPARTMENTS = [
  'Sales',
  'Marketing',
  'Customer Support',
  'Operations',
  'Finance',
  'HR/Recruitment',
  'Product/Engineering',
  'Legal/Compliance'
];

// Small business functions (more relevant for trades/small business)
const SMALL_BUSINESS_FUNCTIONS = [
  { id: 'quoting', label: 'Quoting & Estimates', icon: '📝', description: 'Generate quotes, pricing, job estimates' },
  { id: 'scheduling', label: 'Scheduling & Bookings', icon: '📅', description: 'Appointments, job scheduling, reminders' },
  { id: 'invoicing', label: 'Invoicing & Payments', icon: '💰', description: 'Create invoices, chase payments, receipts' },
  { id: 'customer_comms', label: 'Customer Communication', icon: '💬', description: 'Enquiries, follow-ups, reviews' },
  { id: 'admin', label: 'Admin & Paperwork', icon: '📋', description: 'Forms, certificates, compliance docs' },
  { id: 'marketing', label: 'Marketing & Social', icon: '📣', description: 'Social posts, promotions, website' },
  { id: 'inventory', label: 'Inventory & Supplies', icon: '📦', description: 'Stock tracking, ordering, suppliers' },
  { id: 'staff', label: 'Staff & Rostering', icon: '👥', description: 'Timesheets, rosters, team management' },
];

const HEADCOUNT_OPTIONS = [
  '1-10 employees',
  '11-50 employees',
  '51-200 employees',
  '201-500 employees',
  '500+ employees'
];

const TOOLS = [
  'CRM (Salesforce, HubSpot, etc.)',
  'Email/Calendar (Gmail, Outlook)',
  'Project Management (Asana, Monday, Trello)',
  'Spreadsheets (Excel, Google Sheets)',
  'Accounting software (QuickBooks, Xero)',
  'Custom/in-house systems',
  'Mostly manual processes'
];

// Small business specific tools
const SMALL_BUSINESS_TOOLS = [
  'Email/Calendar (Gmail, Outlook)',
  'Spreadsheets (Excel, Google Sheets)',
  'Accounting software (QuickBooks, Xero, MYOB)',
  'Booking/Scheduling app (Calendly, Timely, ServiceM8)',
  'Social Media (Facebook, Instagram)',
  'WhatsApp/SMS for customers',
  'Job management software (Tradify, Fergus, ServiceTitan)',
  'Mostly manual processes (pen & paper)'
];

const AI_READINESS_OPTIONS = [
  { value: 'skeptical', label: '🔴 Skeptical - We\'re cautious about new technology' },
  { value: 'curious', label: '🟡 Curious - We\'re interested but unsure where to start' },
  { value: 'ready', label: '🟢 Ready - We\'re actively looking for AI solutions' },
  { value: 'advanced', label: '🔵 Advanced - We already use some AI tools' }
];

const BUDGET_RANGES = [
  'Under $5K',
  '$5K - $25K',
  '$25K - $100K',
  '$100K+',
  'Not sure yet'
];

const TIMELINE_GOALS = [
  'Within 1-3 months (Quick wins focus)',
  '3-6 months (Balanced approach)',
  '6-12 months (Strategic transformation)'
];

const MANUAL_HOURS_OPTIONS = [
  '0-10 hours',
  '11-25 hours',
  '26-50 hours',
  '51-100 hours',
  '100+ hours'
];

// ═══════════════════════════════════════════════════════════════════════════════
// INDUSTRY-STANDARD AI READINESS FRAMEWORK
// Based on Cisco AI Readiness Index, OvalEdge Framework, and ITU Standards
// ═══════════════════════════════════════════════════════════════════════════════

const AI_READINESS_PILLARS = {
  data: { name: 'Data Readiness', weight: 0.30 },
  technology: { name: 'Technology Infrastructure', weight: 0.20 },
  talent: { name: 'Talent & Culture', weight: 0.25 },
  strategy: { name: 'Strategy & Governance', weight: 0.15 },
  resources: { name: 'Budget & Resources', weight: 0.10 }
};

// Data Readiness Helps (30% weight)
const DATA_READINESS_QUESTIONS = [
  {
    id: 'data_centralization',
    question: 'How centralized is your business data?',
    options: [
      { value: 1, label: 'Scattered across spreadsheets and individual systems' },
      { value: 2, label: 'Partially centralized with some integration' },
      { value: 3, label: 'Mostly centralized in a few core systems' },
      { value: 4, label: 'Fully centralized with single source of truth' }
    ]
  },
  {
    id: 'data_quality',
    question: 'How would you rate your data quality and consistency?',
    options: [
      { value: 1, label: 'Poor - frequent errors, duplicates, and inconsistencies' },
      { value: 2, label: 'Fair - some quality issues that require manual cleanup' },
      { value: 3, label: 'Good - mostly accurate with occasional issues' },
      { value: 4, label: 'Excellent - clean, consistent, and well-maintained' }
    ]
  },
  {
    id: 'data_accessibility',
    question: 'How easily can your teams access the data they need?',
    options: [
      { value: 1, label: 'Very difficult - requires IT help for most requests' },
      { value: 2, label: 'Somewhat difficult - limited self-service options' },
      { value: 3, label: 'Fairly easy - good self-service with some limitations' },
      { value: 4, label: 'Very easy - comprehensive self-service analytics' }
    ]
  }
];

// Technology Infrastructure Helps (20% weight)
const TECHNOLOGY_QUESTIONS = [
  {
    id: 'cloud_adoption',
    question: 'What is your level of cloud adoption?',
    options: [
      { value: 1, label: 'Minimal - mostly on-premise systems' },
      { value: 2, label: 'Partial - some cloud services (email, storage)' },
      { value: 3, label: 'Significant - core systems are cloud-based' },
      { value: 4, label: 'Cloud-first - fully cloud-native infrastructure' }
    ]
  },
  {
    id: 'system_integration',
    question: 'How well do your systems integrate with each other?',
    options: [
      { value: 1, label: 'Siloed - systems don\'t communicate' },
      { value: 2, label: 'Basic - some manual data transfer between systems' },
      { value: 3, label: 'Good - automated integrations for key workflows' },
      { value: 4, label: 'Excellent - seamless real-time data flow across all systems' }
    ]
  },
  {
    id: 'security_infrastructure',
    question: 'How mature is your security and compliance infrastructure?',
    options: [
      { value: 1, label: 'Basic - minimal security measures' },
      { value: 2, label: 'Developing - some security policies in place' },
      { value: 3, label: 'Established - comprehensive security framework' },
      { value: 4, label: 'Advanced - enterprise-grade security with regular audits' }
    ]
  }
];

// Talent & Culture Helps (25% weight)
const TALENT_QUESTIONS = [
  {
    id: 'ai_expertise',
    question: 'What AI/ML expertise exists in your organization?',
    options: [
      { value: 1, label: 'None - no dedicated AI/data skills' },
      { value: 2, label: 'Basic - some tech-savvy staff, no specialists' },
      { value: 3, label: 'Moderate - data analysts or some AI experience' },
      { value: 4, label: 'Strong - dedicated data science/AI team' }
    ]
  },
  {
    id: 'leadership_buyin',
    question: 'How supportive is leadership of AI initiatives?',
    options: [
      { value: 1, label: 'Skeptical - concerns about AI adoption' },
      { value: 2, label: 'Cautious - open but waiting to see results' },
      { value: 3, label: 'Supportive - actively encouraging AI exploration' },
      { value: 4, label: 'Champion - AI is a strategic priority from the top' }
    ]
  },
  {
    id: 'change_readiness',
    question: 'How does your organization typically handle technology changes?',
    options: [
      { value: 1, label: 'Resistant - change is difficult and slow' },
      { value: 2, label: 'Cautious - changes happen but take time' },
      { value: 3, label: 'Adaptive - reasonable pace of adoption' },
      { value: 4, label: 'Agile - quick to embrace beneficial changes' }
    ]
  }
];

// Strategy & Governance Helps (15% weight)
const STRATEGY_QUESTIONS = [
  {
    id: 'ai_strategy',
    question: 'Does your organization have a clear AI/automation strategy?',
    options: [
      { value: 1, label: 'No - haven\'t considered AI strategy' },
      { value: 2, label: 'Exploring - beginning to think about AI uses' },
      { value: 3, label: 'Defined - clear vision with some planned initiatives' },
      { value: 4, label: 'Mature - comprehensive AI roadmap with KPIs' }
    ]
  },
  {
    id: 'governance_policies',
    question: 'Do you have policies for data privacy and AI ethics?',
    options: [
      { value: 1, label: 'None - no formal policies' },
      { value: 2, label: 'Basic - some informal guidelines' },
      { value: 3, label: 'Established - documented policies in place' },
      { value: 4, label: 'Comprehensive - robust governance framework' }
    ]
  }
];

// Resources Helps (10% weight)
const RESOURCES_QUESTIONS = [
  {
    id: 'budget_commitment',
    question: 'What budget range are you considering for AI initiatives?',
    options: [
      { value: 1, label: 'Under $10K - testing the waters' },
      { value: 2, label: '$10K - $50K - pilot projects' },
      { value: 3, label: '$50K - $200K - significant investment' },
      { value: 4, label: '$200K+ - strategic transformation budget' }
    ]
  },
  {
    id: 'resource_allocation',
    question: 'Can you dedicate staff time to AI implementation and training?',
    options: [
      { value: 1, label: 'No - staff are fully occupied with current duties' },
      { value: 2, label: 'Limited - maybe a few hours per week' },
      { value: 3, label: 'Moderate - can allocate part-time resources' },
      { value: 4, label: 'Yes - can dedicate team members to AI projects' }
    ]
  }
];

const DiagnosticWizard = ({ onComplete }) => {
  const { activeOrganization } = useOrganization();
  const { tier: userPlan } = usePlan();
  const navigate = useNavigate();
  
  // TIER GATING: Check if user is on basic plan
  const isBasicPlan = !userPlan || userPlan === 'basic' || userPlan === 'starter';
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // COMPLETION LOCK SYSTEM
  // Hard lock in sessionStorage that persists across component mounts/unmounts
  // This prevents ANY automatic redirect back to wizard after completion
  // ═══════════════════════════════════════════════════════════════════════════════
  
  const getOrgId = () => activeOrganization?.organization_id || 'default';
  
  const getCompletionLockKey = () => {
    return `assessment_completion_lock_${getOrgId()}`;
  };
  
  const setCompletionLock = (assessmentId, totalOpportunities, comingSoonCount = 0) => {
    const key = getCompletionLockKey();
    const lockData = {
      assessmentId,
      success: true,
      totalOpportunities: totalOpportunities || 0,
      comingSoonCount: comingSoonCount || 0,
      buildableCount: 0, // Will be updated by buildable validation
      tier: userPlan || 'basic',
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 30 * 60 * 1000).toISOString() // 30 min expiry
    };
    sessionStorage.setItem(key, JSON.stringify(lockData));
    console.log('[COMPLETION_LOCK_SET]', { key, assessmentId, totalOpportunities, comingSoonCount, tier: lockData.tier });
    return lockData;
  };
  
  const getCompletionLock = () => {
    try {
      const key = getCompletionLockKey();
      const stored = sessionStorage.getItem(key);
      if (!stored) return null;
      
      const data = JSON.parse(stored);
      // Check if expired (30 minutes)
      if (new Date(data.expiresAt) < new Date()) {
        sessionStorage.removeItem(key);
        console.log('[COMPLETION_LOCK_EXPIRED]', { key });
        return null;
      }
      return data;
    } catch (e) {
      console.error('[COMPLETION_LOCK_ERROR] Failed to read lock:', e);
      return null;
    }
  };
  
  const clearCompletionLock = (reason) => {
    const key = getCompletionLockKey();
    sessionStorage.removeItem(key);
    console.log('[COMPLETION_LOCK_CLEAR]', { reason, key });
  };
  
  const isCompletionLocked = () => {
    const lock = getCompletionLock();
    return lock !== null && lock.success === true;
  };
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // ROUTE-BASED COMPLETION: On completion, navigate to results page with ?success=1
  // The success modal is shown on the Results page, NOT on the wizard
  // This is the single source of truth for completion UX
  // ═══════════════════════════════════════════════════════════════════════════════
  
  // Compact completion snapshot storage (for fallback data on results page)
  const getCompletionSnapshotKey = (assessmentId) => {
    return `assessment_completion_${activeOrganization?.organization_id || 'default'}_${assessmentId}`;
  };
  
  /**
   * ═══════════════════════════════════════════════════════════════════════════════
   * TWO-STATE COMPLETION MODEL:
   * 
   * scanStatus: 'success' | 'error'
   *   - The assessment scan itself completed successfully
   *   - This is the PRIMARY success indicator
   * 
   * buildableStatus: 'success' | 'timeout' | 'error' | 'pending' | 'skipped'
   *   - Whether buildable validation completed
   *   - timeout/error does NOT invalidate the assessment
   *   - Only affects messaging, never resets wizard
   * 
   * Assessment is COMPLETE when scanStatus === 'success', regardless of buildableStatus
   * ═══════════════════════════════════════════════════════════════════════════════
   */
  const saveCompletionSnapshot = (assessmentId, result) => {
    const key = getCompletionSnapshotKey(assessmentId);
    
    // Determine buildable status from backend response
    let buildableStatus = 'pending';
    let buildableCount = null;
    
    if (result.generation_status === 'timed_out' || result.status === 'timed_out') {
      buildableStatus = 'timeout';
    } else if (result.generation_status === 'stopped_early' || result.status === 'stopped_early') {
      buildableStatus = 'timeout'; // Circuit breaker = timeout, not error
    } else if (result.buildable_count !== undefined) {
      buildableStatus = 'success';
      buildableCount = result.buildable_count;
    } else if (result.total_opportunities > 0) {
      // If we have opportunities but no buildable info, it's still processing
      buildableStatus = 'pending';
    } else {
      // Zero opportunities = validation complete, just nothing found
      buildableStatus = 'success';
      buildableCount = 0;
    }
    
    const snapshot = {
      assessmentId,
      // Core counts
      opportunityCountTotal: result.total_opportunities || 0,
      opportunityCountProcess: result.process_opportunities || 0,
      opportunityCountRole: result.role_opportunities || 0,
      comingSoonCount: result.coming_soon_opportunities || 0,
      // TWO-STATE MODEL
      scanStatus: 'success', // We only save snapshot on successful scan
      buildableStatus: buildableStatus,
      buildableCount: buildableCount,
      // Metadata
      tier: userPlan || 'basic',
      stoppedReason: result.stopped_reason || null,
      createdAt: Date.now()
    };
    
    console.log('[COMPLETE] scanStatus=success totalOpps=' + snapshot.opportunityCountTotal);
    console.log('[BUILDABLE] status=' + buildableStatus + ' buildableCount=' + buildableCount);
    console.log('[ASSESSMENT_COMPLETION] Saving snapshot:', { key, snapshot });
    
    sessionStorage.setItem(key, JSON.stringify(snapshot));
    return snapshot;
  };
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // STATE INITIALIZATION - Simple wizard state, no modal management here
  // ═══════════════════════════════════════════════════════════════════════════════
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Upgrade modal state for org chart step
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Flag to prevent render during redirect
  const [isRedirecting, setIsRedirecting] = useState(false);
  
  // Assessment limit state
  const [assessmentLimitReached, setAssessmentLimitReached] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // ASSESSMENT LIMIT CHECK
  // On mount, check if user has reached their monthly assessment limit
  // ═══════════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    const checkAssessmentLimit = async () => {
      if (!activeOrganization) return;
      
      try {
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/assessments/limit-status`,
          {},
          activeOrganization.organization_id
        );
        
        if (response.ok) {
          const data = await response.json();
          setLimitInfo(data);
          if (!data.can_create_assessment) {
            setAssessmentLimitReached(true);
          }
        }
      } catch (error) {
        console.error('[WIZARD] Error checking assessment limit:', error);
      }
    };
    
    checkAssessmentLimit();
  }, [activeOrganization]);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // WIZARD COMPLETION LOCK GUARD
  // On mount, check if completion lock exists and redirect to results
  // This ensures wizard NEVER takes over after completion
  // ═══════════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    console.log('[WIZARD_MOUNT] Checking for completion lock...');
    
    const lock = getCompletionLock();
    if (lock) {
      console.log('[WIZARD_GUARD] Completion lock present — redirecting to results', lock);
      setIsRedirecting(true);
      // DO NOT initialize wizard state, DO NOT set step 1
      // Immediately redirect back to results
      navigate(`/assessments/results/${lock.assessmentId}?success=1`, { replace: true });
      return;
    }
    
    console.log('[WIZARD_MOUNT] No completion lock — initializing wizard');
  }, []);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // REDIRECT PROTECTION - Block any automatic navigation back to wizard
  // Wraps setCurrentStep to prevent reset when completion lock is active
  // ═══════════════════════════════════════════════════════════════════════════════
  
  const safeSetCurrentStep = (step) => {
    if (isCompletionLocked() && step === 1) {
      console.warn('[REDIRECT_BLOCKED] setCurrentStep(1) blocked - completion lock active');
      return;
    }
    setCurrentStep(step);
  };
  
  // DEBUG: Detect component mount/unmount
  useEffect(() => {
    console.log('[AssessmentDiagnostic] MOUNTED at URL:', window.location.href);
    console.log('[AssessmentDiagnostic] Document readyState:', document.readyState);
    
    return () => {
      console.log('[AssessmentDiagnostic] UNMOUNTING at URL:', window.location.href);
    };
  }, []);
  
  const [formData, setFormData] = useState({
    companyName: '',
    industry: '',
    industryOther: '',
    country: '',
    headcount: '',
    departments: [],
    businessFunctions: [],
    toolsUsed: [],
    topBottlenecks: ['', '', ''],
    salaryInput: '',
    salaryType: 'monthly',
    aiReadiness: '',
    budgetRange: '',
    timelineGoal: '',
    manualHoursWeekly: '',
    painPoints: [],
    orgChartFiles: [],
    // AI Readiness Framework - Industry Standard Pillars
    readinessPillars: {
      data: {
        data_centralization: null,
        data_quality: null,
        data_accessibility: null
      },
      technology: {
        cloud_adoption: null,
        system_integration: null,
        security_infrastructure: null
      },
      talent: {
        ai_expertise: null,
        leadership_buyin: null,
        change_readiness: null
      },
      strategy: {
        ai_strategy: null,
        governance_policies: null
      },
      resources: {
        budget_commitment: null,
        resource_allocation: null
      }
    }
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [progressSaved, setProgressSaved] = useState(false);
  const [hasRestoredProgress, setHasRestoredProgress] = useState(false);

  const totalSteps = 15; // Steps: Company(1), Location(2), Depts(3), Tools(4), Bottlenecks(5), Salary(6), 
                         // AI Pillars: Data(7), Tech(8), Talent(9), Strategy(10), Resources(11),
                         // Timeline(12), Hours(13), OrgChart(14), Review(15)
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // SAVE PROGRESS SYSTEM
  // Auto-saves wizard progress to localStorage, allowing users to resume later
  // ═══════════════════════════════════════════════════════════════════════════════
  
  const getProgressKey = () => {
    const orgId = activeOrganization?.organization_id || 'default';
    return `assessment_wizard_progress_${orgId}`;
  };
  
  const saveProgress = (step, data) => {
    try {
      const key = getProgressKey();
      const progressData = {
        currentStep: step,
        formData: data,
        savedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days expiry
      };
      localStorage.setItem(key, JSON.stringify(progressData));
      setProgressSaved(true);
      setTimeout(() => setProgressSaved(false), 2000); // Show indicator for 2s
      console.log('[SAVE_PROGRESS] Saved at step', step);
    } catch (e) {
      console.error('[SAVE_PROGRESS] Failed to save:', e);
    }
  };
  
  const loadProgress = () => {
    try {
      const key = getProgressKey();
      const stored = localStorage.getItem(key);
      if (!stored) return null;
      
      const data = JSON.parse(stored);
      // Check if expired
      if (new Date(data.expiresAt) < new Date()) {
        localStorage.removeItem(key);
        console.log('[LOAD_PROGRESS] Expired, removed');
        return null;
      }
      return data;
    } catch (e) {
      console.error('[LOAD_PROGRESS] Failed to load:', e);
      return null;
    }
  };
  
  const clearProgress = () => {
    try {
      const key = getProgressKey();
      localStorage.removeItem(key);
      console.log('[CLEAR_PROGRESS] Cleared');
    } catch (e) {
      console.error('[CLEAR_PROGRESS] Failed:', e);
    }
  };
  
  // Load saved progress on mount
  useEffect(() => {
    if (hasRestoredProgress) return; // Only restore once
    
    const savedProgress = loadProgress();
    if (savedProgress && savedProgress.currentStep > 1) {
      console.log('[RESTORE_PROGRESS] Found saved progress at step', savedProgress.currentStep);
      setFormData(savedProgress.formData);
      setCurrentStep(savedProgress.currentStep);
      setHasRestoredProgress(true);
    }
  }, [hasRestoredProgress]);
  
  // Auto-save when formData or currentStep changes (debounced)
  useEffect(() => {
    if (!hasRestoredProgress && currentStep === 1) return; // Don't save initial state
    
    const timeoutId = setTimeout(() => {
      saveProgress(currentStep, formData);
    }, 1000); // Debounce 1 second
    
    return () => clearTimeout(timeoutId);
  }, [formData, currentStep, hasRestoredProgress]);
  
  // Adaptive flow: Check if user selected a small business industry
  const isSmallBusiness = isSmallBusinessIndustry(formData.industry);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  // Update readiness pillar scores
  const updateReadinessPillar = (pillar, questionId, value) => {
    setFormData(prev => ({
      ...prev,
      readinessPillars: {
        ...prev.readinessPillars,
        [pillar]: {
          ...prev.readinessPillars[pillar],
          [questionId]: value
        }
      }
    }));
  };

  // Calculate pillar score (average of answered questions, scaled to 100)
  const calculatePillarScore = (pillar) => {
    const pillarData = formData.readinessPillars[pillar];
    const answers = Object.values(pillarData).filter(v => v !== null);
    if (answers.length === 0) return 0;
    const avgScore = answers.reduce((a, b) => a + b, 0) / answers.length;
    return Math.round((avgScore / 4) * 100); // Scale 1-4 to 0-100
  };

  // Calculate overall AI readiness score (weighted average)
  const calculateOverallReadiness = () => {
    let totalScore = 0;
    let totalWeight = 0;
    
    Object.entries(AI_READINESS_PILLARS).forEach(([key, pillar]) => {
      const pillarScore = calculatePillarScore(key);
      const pillarData = formData.readinessPillars[key];
      const answeredCount = Object.values(pillarData).filter(v => v !== null).length;
      
      // Only include pillars with at least one answer
      if (answeredCount > 0) {
        totalScore += pillarScore * pillar.weight;
        totalWeight += pillar.weight;
      }
    });
    
    return totalWeight > 0 ? Math.round(totalScore / totalWeight) : 0;
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      // Track assessment_started when moving from step 1 to step 2
      if (currentStep === 1) {
        trackAssessmentStarted({
          plan_tier: userPlan || 'basic',
          org_id: activeOrganization?.organization_id,
        });
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleOrgChartUpload = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        alert(`${file.name} is too large. Maximum file size is 10MB.`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...validFiles]);
      updateFormData('orgChartFiles', [...uploadedFiles, ...validFiles]);
    }
    
    // Reset input
    e.target.value = '';
  };

  const removeOrgChartFile = (index) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    updateFormData('orgChartFiles', newFiles);
  };

  const handleSubmit = async () => {
    console.log('[ASSESSMENT_SUBMIT] handleSubmit triggered');
    
    // ═══════════════════════════════════════════════════════════════════════════════
    // SET WINDOW FLAG EARLY: Prevent webpack HMR from triggering during submission
    // This fixes the bug where HMR triggers reload during long-running assessment
    // ═══════════════════════════════════════════════════════════════════════════════
    window.__MM_COMPLETION_IN_PROGRESS = true;
    console.log('[ASSESSMENT_SUBMIT] Window flag SET (HMR protection)');
    
    // Set processing state to show loading UI
    setIsProcessing(true);
    console.log('[ASSESSMENT_SUBMIT] isProcessing set to true');
    
    try {
      // Prepare assessment data - ADAPTIVE: Include businessFunctions for small business
      const isSmallBusinessFlow = isSmallBusinessIndustry(formData.industry);
      console.log('[ASSESSMENT_SUBMIT] isSmallBusinessFlow:', isSmallBusinessFlow);
      
      // Calculate readiness scores before submission
      const readinessScores = {
        overall: calculateOverallReadiness(),
        pillars: {}
      };
      Object.keys(AI_READINESS_PILLARS).forEach(key => {
        readinessScores.pillars[key] = calculatePillarScore(key);
      });
      
      const assessmentData = {
        company_name: formData.companyName,
        industry: formData.industry === 'Other' ? formData.industryOther : formData.industry,
        country: formData.country,
        headcount: formData.headcount,
        departments: isSmallBusinessFlow ? [] : formData.departments,
        business_functions: isSmallBusinessFlow ? formData.businessFunctions : [],
        is_small_business: isSmallBusinessFlow,
        pain_points: formData.painPoints,
        tools_used: formData.toolsUsed,
        top_bottlenecks: formData.topBottlenecks.filter(b => b.trim()),
        salary_input: formData.salaryInput ? parseFloat(formData.salaryInput) : null,
        salary_type: formData.salaryType,
        ai_readiness: formData.aiReadiness,
        budget_range: formData.budgetRange,
        timeline_goal: formData.timelineGoal,
        manual_hours_weekly: formData.manualHoursWeekly,
        // Industry-Standard AI Readiness Framework data
        readiness_pillars: formData.readinessPillars,
        readiness_scores: readinessScores
      };
      
      console.log('[ASSESSMENT_SUBMIT] Assessment data prepared:', JSON.stringify(assessmentData).slice(0, 500));

      // UploadSimple org chart files first if any
      let orgChartScanId = null;
      if (uploadedFiles.length > 0) {
        console.log('[Unified Analysis] Uploading org chart files...');
        const uploadResult = await uploadOrgChartFiles(null); // Will generate scan_id
        if (uploadResult.success && uploadResult.scanId) {
          orgChartScanId = uploadResult.scanId;
          console.log('[Unified Analysis] Org chart uploaded with scan_id:', orgChartScanId);
        }
      }

      // Call unified analysis endpoint
      // ========================================================================
      // OPTION 2 UX: Navigate to Results immediately with progressive loading
      // 1. Submit assessment → Get immediate response with assessment_id
      // 2. Navigate to Results page immediately (no modal)
      // 3. Results page shows progress banner while polling
      // 4. Results page handles polling and progressive population
      // ========================================================================
      console.log('[Async Assessment] Starting async assessment submission (Option 2 UX)...');
      
      // Step 1: Submit assessment (returns immediately)
      let submitResponse;
      try {
        submitResponse = await fetchWithOrg(
          `${BACKEND_URL}/api/assessment/complete-async`,
          {
            method: 'POST',
            body: JSON.stringify({
              assessment_data: assessmentData,
              scan_id: orgChartScanId // Include org chart scan_id if uploaded
            })
          },
          activeOrganization?.organization_id
        );
      } catch (fetchError) {
        console.error('[Async Assessment] Network error:', fetchError);
        throw fetchError;
      }

      if (!submitResponse.ok) {
        const errorText = await submitResponse.text().catch(() => 'Unknown error');
        console.error('[Async Assessment] Submit error:', submitResponse.status, errorText);
        throw new Error(`Failed to submit assessment (${submitResponse.status})`);
      }

      const submitResult = await submitResponse.json();
      console.log('[Async Assessment] Assessment submitted:', submitResult);
      
      const assessmentId = submitResult.assessment_id;
      
      // Track assessment started
      trackAssessmentCompleted({
        duration_ms: Date.now() - (window._assessmentStartTime || Date.now()),
        valid_opportunity_count: 0, // Will be populated on results page
        coming_soon_count: 0,
        has_org_chart: uploadedFiles.length > 0,
        stop_reason: 'started_processing',
        plan_tier: userPlan || 'basic',
        org_id: activeOrganization?.organization_id,
      });
      
      // ═══════════════════════════════════════════════════════════════════════════════
      // IMMEDIATE NAVIGATION TO RESULTS (No modal delay)
      // Results page will handle polling and progressive loading with banner
      // ═══════════════════════════════════════════════════════════════════════════════
      console.log('[ASSESSMENT_COMPLETION] Setting completion locks BEFORE navigation');
      
      // DEBUG: Track navigation in localStorage (survives reload)
      localStorage.setItem('mm_debug_last_action', JSON.stringify({
        action: 'assessment_complete_navigate',
        from: window.location.href,
        to: `/assessments/results/${assessmentId}?processing=1`,
        timestamp: new Date().toISOString()
      }));
      
      // Set window-level flag first - survives React remounts
      window.__MM_COMPLETION_IN_PROGRESS = true;
      console.log('[ASSESSMENT_COMPLETION] Window flag SET');
      
      // Set global completion flag - prevents org context reload during navigation
      setCompletionInProgress(assessmentId);
      
      // Set completion lock with initial values
      setCompletionLock(assessmentId, 0, 0);
      
      // Save minimal completion snapshot
      saveCompletionSnapshot(assessmentId, {
        scan_id: assessmentId,
        total_opportunities: 0,
        process_opportunities: 0,
        coming_soon_opportunities: 0,
        role_opportunities: 0,
        generation_status: 'processing'
      });
      
      // Navigate to results page with processing flag (no modal shown)
      const resultsPath = `/assessments/results/${assessmentId}?processing=1`;
      console.log('🚀 [ASSESSMENT_COMPLETE] Navigating to results page immediately');
      console.log('🚀 [ASSESSMENT_COMPLETE] Target URL:', resultsPath);
      
      // Clear saved progress on successful submission
      clearProgress();
      
      navigate(resultsPath, { replace: true });
      console.log('🚀 [ASSESSMENT_COMPLETE] navigate() called - should be navigating now');
      
    } catch (error) {
      console.error('Error submitting assessment:', error);
      // Clear ALL completion flags on error including window flag
      window.__MM_COMPLETION_IN_PROGRESS = false;
      clearCompletionInProgress();
      alert('Failed to complete assessment. Please try again.');
      setIsProcessing(false);
    }
  };

  const uploadOrgChartFiles = async (scanId) => {
    try {
      // Generate a scan_id if not provided
      const uploadScanId = scanId || `scan_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const formData = new FormData();
      
      // Append each file
      uploadedFiles.forEach((file, index) => {
        formData.append('files', file);
      });

      // Add scan_id and organization_id
      formData.append('scan_id', uploadScanId);
      if (activeOrganization?.organization_id) {
        formData.append('organization_id', activeOrganization.organization_id);
      }

      const response = await fetch(`${BACKEND_URL}/api/org/org-chart/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const errMsg = typeof errData.detail === 'string' ? errData.detail : 'Failed to upload org chart files';
        console.error(errMsg);
        return { success: false, error: errMsg };
      }

      const uploadResult = await response.json();
      return { success: true, scanId: uploadScanId };
    } catch (error) {
      console.error('Error uploading org chart files:', error);
      return { success: false };
    }
  };

  // handleNewScan is only called from Results page now (via URL navigation)
  // Only clear completion lock when user explicitly requests new scan
  const handleNewScan = () => {
    console.log('[ASSESSMENT_WIZARD] Starting new scan - clearing completion lock');
    clearCompletionLock('button_click_new_scan');
    setCurrentStep(1);
    setUploadedFiles([]);
    setFormData({
      companyName: '',
      industry: '',
      industryOther: '',
      country: '',
      headcount: '',
      departments: [],
      businessFunctions: [],
      toolsUsed: [],
      topBottlenecks: ['', '', ''],
      salaryInput: '',
      salaryType: 'monthly',
      aiReadiness: '',
      budgetRange: '',
      timelineGoal: '',
      manualHoursWeekly: '',
      painPoints: [],
      orgChartFiles: []
    });
  };

  // handleMapProcess is called from Results page modal now

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.companyName && formData.industry && 
               (formData.industry !== 'Other' || formData.industryOther);
      case 2:
        return formData.country && formData.headcount;
      case 3:
        // ADAPTIVE: Check businessFunctions for small business, departments for corporate
        return isSmallBusiness 
          ? formData.businessFunctions.length > 0 
          : formData.departments.length > 0;
      case 4:
        return formData.toolsUsed.length > 0;
      case 5:
        return formData.topBottlenecks[0].trim();
      case 6:
        return true; // Salary - Optional
      // AI READINESS PILLARS (cases 7-11)
      case 7: { // Data Readiness - require at least 2 of 3 questions answered
        const dataAnswers = Object.values(formData.readinessPillars.data).filter(v => v !== null);
        return dataAnswers.length >= 2;
      }
      case 8: { // Technology - require at least 2 of 3 questions answered
        const techAnswers = Object.values(formData.readinessPillars.technology).filter(v => v !== null);
        return techAnswers.length >= 2;
      }
      case 9: { // Talent - require at least 2 of 3 questions answered  
        const talentAnswers = Object.values(formData.readinessPillars.talent).filter(v => v !== null);
        return talentAnswers.length >= 2;
      }
      case 10: { // Strategy - require at least 1 of 2 questions answered
        const strategyAnswers = Object.values(formData.readinessPillars.strategy).filter(v => v !== null);
        return strategyAnswers.length >= 1;
      }
      case 11: { // Resources - require at least 1 of 2 questions answered
        const resourcesAnswers = Object.values(formData.readinessPillars.resources).filter(v => v !== null);
        return resourcesAnswers.length >= 1;
      }
      case 12:
        return formData.timelineGoal; // Timeline Goals - required
      case 13:
        return formData.manualHoursWeekly; // Manual Hours - required
      case 14:
        return true; // Org chart upload - optional
      case 15:
        return true; // Review step - always valid
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white mb-2">Let&apos;s start with the basics</h2>
            <p className="text-[#A0AEC0]">Tell us about your company</p>
            
            {/* Assessment framing note */}
            <div className="p-4 rounded-xl bg-[#00E5C0]/10 border border-[#00E5C0]/30 mb-2">
              <p className="text-sm text-[#A0AEC0]">
                <strong className="text-white">How we use this:</strong> Your answers help us identify 
                relevant opportunities and estimate when you could realistically implement them. This 
                is about guiding your next steps — not judging your business.{' '}
                <a href="/how-we-score" target="_blank" rel="noopener noreferrer" className="text-[#00E5C0] hover:underline">
                  Learn more
                </a>
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
                Company Name *
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => updateFormData('companyName', e.target.value)}
                className="w-full px-4 py-3 rounded-lg glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0]"
                placeholder="Enter your company name"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-[#A0AEC0] mb-2">
                Industry *
                <Tooltip text="Choose the industry that best describes your business. This helps us identify relevant automation opportunities." />
              </label>
              <select
                value={formData.industry}
                onChange={(e) => {
                  const val = e.target.value;
                  // Don't select category headers
                  if (val.startsWith('──')) return;
                  updateFormData('industry', val);
                }}
                className="w-full px-4 py-3 rounded-lg glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0]"
                style={{
                  colorScheme: 'dark'
                }}
              >
                <option value="" className="bg-[#1a1a2e] text-white">Select industry</option>
                {/* Corporate Industries */}
                <option disabled className="bg-[#0a0a1a] text-[#00E5C0] font-semibold">── Enterprise & Corporate ──</option>
                {INDUSTRY_CATEGORIES.corporate.map(ind => (
                  <option key={ind} value={ind} className="bg-[#1a1a2e] text-white">{ind}</option>
                ))}
                {/* Trades & Construction */}
                <option disabled className="bg-[#0a0a1a] text-[#10B981] font-semibold">── Trades & Construction ──</option>
                {INDUSTRY_CATEGORIES.trades.map(ind => (
                  <option key={ind} value={ind} className="bg-[#1a1a2e] text-white">🔧 {ind}</option>
                ))}
                {/* Small Business & Services */}
                <option disabled className="bg-[#0a0a1a] text-[#00E5C0] font-semibold">── Small Business & Services ──</option>
                {INDUSTRY_CATEGORIES.smallBusiness.map(ind => (
                  <option key={ind} value={ind} className="bg-[#1a1a2e] text-white">🏪 {ind}</option>
                ))}
                {/* Health & Wellness */}
                <option disabled className="bg-[#0a0a1a] text-[#14F195] font-semibold">── Health & Wellness ──</option>
                {INDUSTRY_CATEGORIES.healthServices.map(ind => (
                  <option key={ind} value={ind} className="bg-[#1a1a2e] text-white">🏥 {ind}</option>
                ))}
                {/* Other */}
                <option disabled className="bg-[#0a0a1a] text-gray-500">────────────</option>
                <option value="Other" className="bg-[#1a1a2e] text-white">Other (please specify)</option>
              </select>
              {/* Small business indicator */}
              {isSmallBusiness && (
                <p className="text-xs text-[#10B981] mt-2 flex items-center gap-1">
                  ✓ We&apos;ve tailored the assessment for small business &amp; trades
                </p>
              )}
            </div>

            {formData.industry === 'Other' && (
              <div>
                <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
                  Please specify *
                </label>
                <input
                  type="text"
                  value={formData.industryOther}
                  onChange={(e) => updateFormData('industryOther', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0]"
                  placeholder="Enter your industry"
                />
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white mb-2">Location & Size</h2>
            <p className="text-[#A0AEC0]">Question us understand your company&apos;s scale</p>
            
            <div>
              <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
                Country/Region *
              </label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => updateFormData('country', e.target.value)}
                className="w-full px-4 py-3 rounded-lg glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0]"
                placeholder="e.g., United States, United Kingdom"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
                Total Headcount *
              </label>
              <select
                value={formData.headcount}
                onChange={(e) => updateFormData('headcount', e.target.value)}
                className="w-full px-4 py-3 rounded-lg glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0]"
                style={{
                  colorScheme: 'dark'
                }}
              >
                <option value="" className="bg-[#1a1a2e] text-white">Select headcount</option>
                {HEADCOUNT_OPTIONS.map(opt => (
                  <option key={opt} value={opt} className="bg-[#1a1a2e] text-white">{opt}</option>
                ))}
              </select>
            </div>
          </div>
        );

      case 3:
        // ADAPTIVE FLOW: Show different options for small business vs corporate
        return isSmallBusiness ? (
          // SMALL BUSINESS: Show Business Functions
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white mb-2">What Do You Need Question With?</h2>
            <p className="text-[#A0AEC0]">Select the areas where you spend the most time or want to improve (Select all that apply)</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {SMALL_BUSINESS_FUNCTIONS.map(func => (
                <button
                  key={func.id}
                  onClick={() => toggleArrayItem('businessFunctions', func.id)}
                  className={`p-4 rounded-lg text-left transition-all ${
                    formData.businessFunctions.includes(func.id)
                      ? 'bg-gradient-to-r from-[#10B981]/30 to-[#00E5C0]/30 border-2 border-[#10B981]'
                      : 'glass hover:bg-[rgba(255,255,255,0.05)]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-1 shrink-0 ${
                      formData.businessFunctions.includes(func.id)
                        ? 'bg-[#10B981] border-[#10B981]'
                        : 'border-[#718096]'
                    }`}>
                      {formData.businessFunctions.includes(func.id) && (
                        <CheckCircle size={14} className="text-white" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{func.icon}</span>
                        <span className="text-white font-medium">{func.label}</span>
                      </div>
                      <p className="text-xs text-[#718096] mt-1">{func.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          // CORPORATE: Show Traditional Departments
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white mb-2">Primary Departments</h2>
            <p className="text-[#A0AEC0]">Which departments exist in your business? (Select all that apply)</p>
            
            <div className="grid grid-cols-2 gap-4">
              {DEPARTMENTS.map(dept => (
                <button
                  key={dept}
                  onClick={() => toggleArrayItem('departments', dept)}
                  className={`p-4 rounded-lg text-left transition-all ${
                    formData.departments.includes(dept)
                      ? 'bg-gradient-to-r from-[#00E5C0]/30 to-[#00E5C0]/30 border-2 border-[#00E5C0]'
                      : 'glass hover:bg-[rgba(255,255,255,0.05)]'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      formData.departments.includes(dept)
                        ? 'bg-[#00E5C0] border-[#00E5C0]'
                        : 'border-[#718096]'
                    }`}>
                      {formData.departments.includes(dept) && (
                        <CheckCircle size={14} className="text-white" />
                      )}
                    </div>
                    <span className="text-white font-medium">{dept}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 4:
        // ADAPTIVE FLOW: Show different tools based on business type
        const toolsList = isSmallBusiness ? SMALL_BUSINESS_TOOLS : TOOLS;
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white mb-2">Current Tools & Layers</h2>
            <p className="text-[#A0AEC0]">What tools does your {isSmallBusiness ? 'business' : 'team'} currently use?</p>
            
            <div className="space-y-3">
              {toolsList.map(tool => (
                <button
                  key={tool}
                  onClick={() => toggleArrayItem('toolsUsed', tool)}
                  className={`w-full p-4 rounded-lg text-left transition-all ${
                    formData.toolsUsed.includes(tool)
                      ? 'bg-gradient-to-r from-[#00E5C0]/30 to-[#00E5C0]/30 border-2 border-[#00E5C0]'
                      : 'glass hover:bg-[rgba(255,255,255,0.05)]'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      formData.toolsUsed.includes(tool)
                        ? 'bg-[#00E5C0] border-[#00E5C0]'
                        : 'border-[#718096]'
                    }`}>
                      {formData.toolsUsed.includes(tool) && (
                        <CheckCircle size={14} className="text-white" />
                      )}
                    </div>
                    <span className="text-white">{tool}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">Top 3 Bottlenecks</h2>
                <p className="text-[#A0AEC0]">What are your top operational bottlenecks or time sinks?</p>
              </div>
              <Tooltip text="List specific tasks that take too much time or cause delays. Examples: Manual data entry, approval workflows, report generation." />
            </div>
            
            {[0, 1, 2].map(index => (
              <div key={index}>
                <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
                  Bottleneck {index + 1} {index === 0 && '*'}
                </label>
                <textarea
                  value={formData.topBottlenecks[index]}
                  onChange={(e) => {
                    const newBottlenecks = [...formData.topBottlenecks];
                    newBottlenecks[index] = e.target.value;
                    updateFormData('topBottlenecks', newBottlenecks);
                  }}
                  className="w-full px-4 py-3 rounded-lg glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0] resize-none"
                  rows={3}
                  placeholder="Describe a time-consuming or inefficient process..."
                />
              </div>
            ))}
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white mb-2">Average Salary/Rate</h2>
            <p className="text-[#A0AEC0]">This helps us calculate potential cost savings</p>
            
            <div>
              <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
                Salary Type
              </label>
              <select
                value={formData.salaryType}
                onChange={(e) => updateFormData('salaryType', e.target.value)}
                className="w-full px-4 py-3 rounded-lg glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0]"
                style={{
                  colorScheme: 'dark'
                }}
              >
                <option value="monthly" className="bg-[#1a1a2e] text-white">Monthly Salary</option>
                <option value="hourly" className="bg-[#1a1a2e] text-white">Hourly Rate</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
                Average {formData.salaryType === 'monthly' ? 'Monthly Salary' : 'Hourly Rate'} (optional)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#718096]">$</span>
                <input
                  type="number"
                  value={formData.salaryInput}
                  onChange={(e) => updateFormData('salaryInput', e.target.value)}
                  className="w-full pl-8 pr-4 py-3 rounded-lg glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0]"
                  placeholder="Enter amount"
                />
              </div>
              <p className="text-xs text-[#718096] mt-2">
                Leave blank to use regional defaults
              </p>
            </div>
          </div>
        );

      // ═══════════════════════════════════════════════════════════════════════════════
      // AI READINESS PILLARS - Compact dropdown version for reduced height
      // ═══════════════════════════════════════════════════════════════════════════════
      
      case 7: // DATA READINESS (30% weight)
        return (
          <div className="space-y-5">
            <div className="text-center mb-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00E5C0]/20 rounded-full mb-3">
                <ChartBar size={20} className="text-[#00E5C0]" />
                <span className="text-white font-semibold text-sm">AI Readiness: Pillar 1 of 5</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-1">Data Readiness</h2>
              <p className="text-[#A0AEC0] text-sm">30% of your overall AI readiness score</p>
            </div>
            
            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-2 mb-4">
              {['data', 'technology', 'talent', 'strategy', 'resources'].map((p, idx) => (
                <div 
                  key={p}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    idx === 0 ? 'bg-[#00E5C0] scale-125' : 'bg-[rgba(255,255,255,0.2)]'
                  }`}
                />
              ))}
            </div>
            
            <div className="space-y-4">
              {DATA_READINESS_QUESTIONS.map(q => (
                <div key={q.id} className="glass rounded-xl p-4">
                  <label className="block text-white font-medium text-sm mb-2">{q.question}</label>
                  <select
                    value={formData.readinessPillars.data[q.id] || ''}
                    onChange={(e) => updateReadinessPillar('data', q.id, parseInt(e.target.value))}
                    className="w-full px-4 py-3 rounded-lg glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0] cursor-pointer"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" disabled className="bg-[#1a1a2e] text-[#718096]">Select an option...</option>
                    {q.options.map(opt => (
                      <option key={opt.value} value={opt.value} className="bg-[#1a1a2e] text-white">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            
            <div className="text-center text-sm text-[#A0AEC0] pt-2">
              Current pillar score: <span className="text-[#00E5C0] font-bold">{calculatePillarScore('data')}%</span>
            </div>
          </div>
        );

      case 8: // TECHNOLOGY INFRASTRUCTURE (20% weight)
        return (
          <div className="space-y-5">
            <div className="text-center mb-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00E5C0]/20 rounded-full mb-3">
                <span className="text-xl">⚙️</span>
                <span className="text-white font-semibold text-sm">AI Readiness: Pillar 2 of 5</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-1">Technology Infrastructure</h2>
              <p className="text-[#A0AEC0] text-sm">20% of your overall AI readiness score</p>
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-4">
              {['data', 'technology', 'talent', 'strategy', 'resources'].map((p, idx) => (
                <div 
                  key={p}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    idx === 1 ? 'bg-[#00E5C0] scale-125' : idx < 1 ? 'bg-[#22C55E]' : 'bg-[rgba(255,255,255,0.2)]'
                  }`}
                />
              ))}
            </div>
            
            <div className="space-y-4">
              {TECHNOLOGY_QUESTIONS.map(q => (
                <div key={q.id} className="glass rounded-xl p-4">
                  <label className="block text-white font-medium text-sm mb-2">{q.question}</label>
                  <select
                    value={formData.readinessPillars.technology[q.id] || ''}
                    onChange={(e) => updateReadinessPillar('technology', q.id, parseInt(e.target.value))}
                    className="w-full px-4 py-3 rounded-lg glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0] cursor-pointer"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" disabled className="bg-[#1a1a2e] text-[#718096]">Select an option...</option>
                    {q.options.map(opt => (
                      <option key={opt.value} value={opt.value} className="bg-[#1a1a2e] text-white">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            
            <div className="text-center text-sm text-[#A0AEC0] pt-2">
              Current pillar score: <span className="text-[#00E5C0] font-bold">{calculatePillarScore('technology')}%</span>
            </div>
          </div>
        );

      case 9: // TALENT & CULTURE (25% weight)
        return (
          <div className="space-y-5">
            <div className="text-center mb-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00E5C0]/20 rounded-full mb-3">
                <UsersThree size={20} className="text-[#00E5C0]" />
                <span className="text-white font-semibold text-sm">AI Readiness: Pillar 3 of 5</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-1">Talent & Culture</h2>
              <p className="text-[#A0AEC0] text-sm">25% of your overall AI readiness score</p>
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-4">
              {['data', 'technology', 'talent', 'strategy', 'resources'].map((p, idx) => (
                <div 
                  key={p}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    idx === 2 ? 'bg-[#00E5C0] scale-125' : idx < 2 ? 'bg-[#22C55E]' : 'bg-[rgba(255,255,255,0.2)]'
                  }`}
                />
              ))}
            </div>
            
            <div className="space-y-4">
              {TALENT_QUESTIONS.map(q => (
                <div key={q.id} className="glass rounded-xl p-4">
                  <label className="block text-white font-medium text-sm mb-2">{q.question}</label>
                  <select
                    value={formData.readinessPillars.talent[q.id] || ''}
                    onChange={(e) => updateReadinessPillar('talent', q.id, parseInt(e.target.value))}
                    className="w-full px-4 py-3 rounded-lg glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0] cursor-pointer"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" disabled className="bg-[#1a1a2e] text-[#718096]">Select an option...</option>
                    {q.options.map(opt => (
                      <option key={opt.value} value={opt.value} className="bg-[#1a1a2e] text-white">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            
            <div className="text-center text-sm text-[#A0AEC0] pt-2">
              Current pillar score: <span className="text-[#00E5C0] font-bold">{calculatePillarScore('talent')}%</span>
            </div>
          </div>
        );

      case 10: // STRATEGY & GOVERNANCE (15% weight)
        return (
          <div className="space-y-5">
            <div className="text-center mb-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00E5C0]/20 rounded-full mb-3">
                <Target size={20} className="text-[#00E5C0]" />
                <span className="text-white font-semibold text-sm">AI Readiness: Pillar 4 of 5</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-1">Strategy & Governance</h2>
              <p className="text-[#A0AEC0] text-sm">15% of your overall AI readiness score</p>
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-4">
              {['data', 'technology', 'talent', 'strategy', 'resources'].map((p, idx) => (
                <div 
                  key={p}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    idx === 3 ? 'bg-[#00E5C0] scale-125' : idx < 3 ? 'bg-[#22C55E]' : 'bg-[rgba(255,255,255,0.2)]'
                  }`}
                />
              ))}
            </div>
            
            <div className="space-y-4">
              {STRATEGY_QUESTIONS.map(q => (
                <div key={q.id} className="glass rounded-xl p-4">
                  <label className="block text-white font-medium text-sm mb-2">{q.question}</label>
                  <select
                    value={formData.readinessPillars.strategy[q.id] || ''}
                    onChange={(e) => updateReadinessPillar('strategy', q.id, parseInt(e.target.value))}
                    className="w-full px-4 py-3 rounded-lg glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0] cursor-pointer"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" disabled className="bg-[#1a1a2e] text-[#718096]">Select an option...</option>
                    {q.options.map(opt => (
                      <option key={opt.value} value={opt.value} className="bg-[#1a1a2e] text-white">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            
            <div className="text-center text-sm text-[#A0AEC0] pt-2">
              Current pillar score: <span className="text-[#00E5C0] font-bold">{calculatePillarScore('strategy')}%</span>
            </div>
          </div>
        );

      case 11: // BUDGET & RESOURCES (10% weight)
        return (
          <div className="space-y-5">
            <div className="text-center mb-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00E5C0]/20 rounded-full mb-3">
                <CurrencyDollar size={20} className="text-[#00E5C0]" />
                <span className="text-white font-semibold text-sm">AI Readiness: Pillar 5 of 5</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-1">Budget & Resources</h2>
              <p className="text-[#A0AEC0] text-sm">10% of your overall AI readiness score</p>
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-4">
              {['data', 'technology', 'talent', 'strategy', 'resources'].map((p, idx) => (
                <div 
                  key={p}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    idx === 4 ? 'bg-[#00E5C0] scale-125' : 'bg-[#22C55E]'
                  }`}
                />
              ))}
            </div>
            
            <div className="space-y-4">
              {RESOURCES_QUESTIONS.map(q => (
                <div key={q.id} className="glass rounded-xl p-4">
                  <label className="block text-white font-medium text-sm mb-2">{q.question}</label>
                  <select
                    value={formData.readinessPillars.resources[q.id] || ''}
                    onChange={(e) => updateReadinessPillar('resources', q.id, parseInt(e.target.value))}
                    className="w-full px-4 py-3 rounded-lg glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0] cursor-pointer"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" disabled className="bg-[#1a1a2e] text-[#718096]">Select an option...</option>
                    {q.options.map(opt => (
                      <option key={opt.value} value={opt.value} className="bg-[#1a1a2e] text-white">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            
            {/* Final Score Summary - More compact */}
            <div className="glass rounded-xl p-4 text-center mt-4">
              <p className="text-[#A0AEC0] text-sm mb-1">Your Overall AI Readiness Score</p>
              <div className="text-3xl font-bold text-white mb-1">{calculateOverallReadiness()}<span className="text-lg text-[#A0AEC0]">/100</span></div>
              <p className="text-sm text-[#00E5C0]">
                {calculateOverallReadiness() >= 80 ? 'AI Ready' : 
                 calculateOverallReadiness() >= 60 ? 'Good Potential' : 
                 calculateOverallReadiness() >= 40 ? 'Getting Started' : 'Foundation Building'}
              </p>
            </div>
          </div>
        );

      case 12: // TIMELINE GOALS
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white mb-2">Timeline Goals</h2>
            <p className="text-[#A0AEC0]">When would you like to start seeing results?</p>
            
            <div className="space-y-3">
              {TIMELINE_GOALS.map(goal => (
                <button
                  key={goal}
                  onClick={() => updateFormData('timelineGoal', goal)}
                  className={`w-full p-4 rounded-lg text-left transition-all ${
                    formData.timelineGoal === goal
                      ? 'bg-gradient-to-r from-[#00E5C0]/30 to-[#00E5C0]/30 border-2 border-[#00E5C0]'
                      : 'glass hover:bg-[rgba(255,255,255,0.05)]'
                  }`}
                >
                  <span className="text-white">{goal}</span>
                </button>
              ))}
            </div>
          </div>
        );

      // Removed duplicate case 12

      case 13: // MANUAL HOURS
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white mb-2">Time & Volume Context</h2>
            <p className="text-[#A0AEC0]">Roughly how many hours per week does your team spend on manual/repetitive tasks?</p>
            
            <div className="space-y-3">
              {MANUAL_HOURS_OPTIONS.map(hours => (
                <button
                  key={hours}
                  onClick={() => updateFormData('manualHoursWeekly', hours)}
                  className={`w-full p-4 rounded-lg text-left transition-all ${
                    formData.manualHoursWeekly === hours
                      ? 'bg-gradient-to-r from-[#00E5C0]/30 to-[#00E5C0]/30 border-2 border-[#00E5C0]'
                      : 'glass hover:bg-[rgba(255,255,255,0.05)]'
                  }`}
                >
                  <span className="text-white text-lg">{hours}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 14: // ORG CHART UPLOAD
        // TIER GATING: Basic users see a clean "Builder Feature Preview Panel"
        if (isBasicPlan) {
          return (
            <div className="space-y-6">
              {/* Header with Builder Badge */}
              <div className="flex items-center gap-3">
                <h2 className="text-3xl font-bold text-white">Organization Chart</h2>
                <span className="px-3 py-1 rounded-full bg-gradient-to-r from-[#00E5C0]/20 to-[#00E5C0]/20 border border-[#00E5C0]/30 text-xs font-medium text-[#A0AEC0] flex items-center gap-1.5">
                  <Lock size={12} />
                  Builder Feature
                </span>
              </div>
              <p className="text-[#A0AEC0]">Org chart analysis is available on Builder and higher plans.</p>
              
              {/* Builder Feature Preview Panel */}
              <div className="glass rounded-2xl p-6 border border-white/10">
                {/* Value Proposition */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Sparkle size={18} className="text-[#00E5C0]" />
                    What org chart analysis unlocks:
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#00E5C0]/20 flex items-center justify-center flex-shrink-0">
                        <UsersThree size={16} className="text-[#00E5C0]" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">Role-based AI opportunities</p>
                        <p className="text-xs text-[#718096]">Tailored automation for each role</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#00E5C0]/20 flex items-center justify-center flex-shrink-0">
                        <Target size={16} className="text-[#00E5C0]" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">Executive vs team insights</p>
                        <p className="text-xs text-[#718096]">Strategic and operational views</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#14F195]/20 flex items-center justify-center flex-shrink-0">
                        <ChartBar size={16} className="text-[#14F195]" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">Visual opportunity mapping</p>
                        <p className="text-xs text-[#718096]">See AI potential across your org</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center flex-shrink-0">
                        <Lightning size={16} className="text-[#F59E0B]" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">Cross-role automation</p>
                        <p className="text-xs text-[#718096]">Detect handoff inefficiencies</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-white/10">
                  <button
                    onClick={() => setCurrentStep(15)}
                    className="flex-1 px-6 py-3 rounded-xl bg-[#00E5C0] text-white font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                  >
                    Continue without org chart
                    <ArrowRight size={18} />
                  </button>
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="flex-1 px-6 py-3 rounded-xl glass border border-[#00E5C0]/30 text-white font-medium hover:bg-[#00E5C0]/10 transition-colors flex items-center justify-center gap-2"
                  >
                    <Sparkle size={16} className="text-[#00E5C0]" />
                    Upgrade to Builder
                  </button>
                </div>
              </div>

              <p className="text-sm text-[#718096] text-center">
                You can always upgrade later to unlock these features for your completed assessment.
              </p>
            </div>
          );
        }
        
        // Builder/Pro/Partner Pro users get full access
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white mb-2">Organization Chart (Optional)</h2>
            <p className="text-[#A0AEC0]">UploadSimple your org chart to enable AI-powered analysis and visualize AI opportunities across your organization</p>
            
            {/* Credit cost estimate */}
            <CreditCostEstimate cost={60} label="org chart analysis" />
            
            <div className="glass rounded-xl p-8 border-2 border-dashed border-[#00E5C0]/30 hover:border-[#00E5C0]/60 transition-colors">
              <input
                type="file"
                id="orgChartUpload"
                multiple
                accept="image/*,.pdf"
                onChange={handleOrgChartUpload}
                className="hidden"
              />
              
              <label
                htmlFor="orgChartUpload"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <div className="w-16 h-16 rounded-full bg-[#00E5C0] flex items-center justify-center mb-4">
                  <UploadSimple size={32} className="text-white" />
                </div>
                <p className="text-white font-medium mb-2">Click to upload or drag and drop</p>
                <p className="text-sm text-[#A0AEC0]">PNG, JPG, PDF (Max 10MB per file)</p>
              </label>
            </div>

            {uploadedFiles.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm text-[#A0AEC0]">Uploaded Files ({uploadedFiles.length})</p>
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="glass rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileImage size={20} className="text-[#00E5C0]" />
                      <div>
                        <p className="text-white text-sm font-medium">{file.name}</p>
                        <p className="text-xs text-[#A0AEC0]">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeOrgChartFile(index)}
                      className="p-1 hover:bg-[rgba(239,68,68,0.2)] rounded transition-colors"
                    >
                      <X size={18} className="text-[#EF4444]" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {isUploading && (
              <div className="flex items-center justify-center gap-2 text-[#00E5C0]">
                <CircleNotch size={20} className="animate-spin" />
                <span className="text-sm">Uploading...</span>
              </div>
            )}

            <div className="bg-[#00E5C0]/10 rounded-xl p-4 border border-[#00E5C0]/30">
              <p className="text-sm text-[#A0AEC0]">
                💡 <span className="text-white font-medium">Pro tip:</span> UploadSimple multiple pages if your org chart is spread across several documents. This helps our AI understand your complete organizational structure.
              </p>
            </div>
          </div>
        );

      case 15: // REVIEW STEP
        // ADAPTIVE: Show different summary labels based on business type
        const focusAreasLabel = isSmallBusiness ? 'Focus Areas' : 'Departments';
        const focusAreasCount = isSmallBusiness 
          ? formData.businessFunctions.length 
          : formData.departments.length;
        
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white mb-2">Ready to Analyze!</h2>
            <p className="text-[#A0AEC0]">We&apos;ll analyze your business and identify AI automation opportunities</p>
            
            {/* Credit cost estimate */}
            <CreditCostEstimate cost={15} label="standard assessment" />
            
            <div className="glass rounded-xl p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[#718096]">Company</p>
                  <p className="text-white font-medium">{formData.companyName}</p>
                </div>
                <div>
                  <p className="text-[#718096]">Industry</p>
                  <p className="text-white font-medium">
                    {formData.industry === 'Other' ? formData.industryOther : formData.industry}
                    {isSmallBusiness && <span className="ml-2 text-[#10B981]">🔧</span>}
                  </p>
                </div>
                <div>
                  <p className="text-[#718096]">Size</p>
                  <p className="text-white font-medium">{formData.headcount}</p>
                </div>
                <div>
                  <p className="text-[#718096]">{focusAreasLabel}</p>
                  <p className="text-white font-medium">{focusAreasCount} selected</p>
                </div>
                <div>
                  <p className="text-[#718096]">Tools</p>
                  <p className="text-white font-medium">{formData.toolsUsed.length} selected</p>
                </div>
                <div>
                  <p className="text-[#718096]">Org Charts</p>
                  <p className="text-white font-medium">{uploadedFiles.length} uploaded</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-[#00E5C0]/10 to-[#00E5C0]/10 rounded-xl p-6 border border-[#00E5C0]/30">
              <p className="text-white text-center">
                Our AI will analyze your responses and generate a personalized opportunity report
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // REDIRECT GUARD: Don't render wizard content if redirecting to results
  // ═══════════════════════════════════════════════════════════════════════════════
  if (isRedirecting) {
    console.log('[WIZARD_RENDER] Blocked - redirecting to results');
    return (
      <div className="max-w-3xl mx-auto py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#00E5C0]/20 flex items-center justify-center">
            <CircleNotch size={24} className="text-[#00E5C0] animate-spin" />
          </div>
          <p className="text-[#A0AEC0]">Loading your results...</p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // ASSESSMENT LIMIT REACHED: Show upgrade prompt instead of wizard
  // ═══════════════════════════════════════════════════════════════════════════════
  if (assessmentLimitReached && limitInfo) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4">
        <div className="glass rounded-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#00E5C0]/20 flex items-center justify-center">
            <Lock size={32} className="text-[#00E5C0]" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Monthly Assessment Limit Reached</h2>
          <p className="text-[#A0AEC0] mb-6">
            You&apos;ve used your {limitInfo.limit} free assessment{limitInfo.limit > 1 ? 's' : ''} for this month. 
            Your limit resets in <span className="text-white font-semibold">{limitInfo.days_until_reset} day{limitInfo.days_until_reset !== 1 ? 's' : ''}</span>.
          </p>
          
          <div className="p-4 rounded-xl bg-[#00E5C0]/10 border border-[#00E5C0]/20 mb-6">
            <p className="text-sm text-[#A0AEC0]">
              <span className="text-[#00E5C0] font-semibold">Tip:</span> Upgrade to Builder for more assessments, 
              full opportunity details, uploads, and more.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/billing')}
              className="px-8 py-3 rounded-full bg-[#00E5C0] text-white font-semibold hover:opacity-90 transition-opacity"
            >
              Upgrade to Builder
            </button>
            <button
              onClick={() => navigate('/partner-pro/assessments')}
              className="px-8 py-3 rounded-full glass text-white font-semibold hover:bg-[rgba(255,255,255,0.1)] transition-colors"
            >
              View My Assessments
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="max-w-3xl mx-auto py-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-[#A0AEC0]">Step {currentStep} of {totalSteps}</span>
            <div className="flex items-center gap-3">
              {/* Progress Saved Indicator */}
              <AnimatePresence>
                {progressSaved && (
                  <motion.span
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    className="text-xs text-[#14F195] flex items-center gap-1"
                  >
                    <CheckCircle size={12} />
                    Progress saved
                  </motion.span>
                )}
              </AnimatePresence>
              <span className="text-sm text-[#A0AEC0]">{Math.round((currentStep / totalSteps) * 100)}% Complete</span>
            </div>
          </div>
          <div className="h-2 bg-[#1A1B23] rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-[#00E5C0]"
              initial={{ width: 0 }}
              animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          
          {/* Resume Progress Banner - Show only when progress was restored */}
          {hasRestoredProgress && currentStep > 1 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 px-4 py-2 rounded-lg bg-[#00E5C0]/10 border border-[#00E5C0]/30 flex items-center justify-between"
            >
              <span className="text-sm text-[#A0AEC0]">
                <span className="text-white font-medium">Welcome back!</span> We restored your progress from where you left off.
              </span>
              <button
                onClick={() => {
                  clearProgress();
                  setFormData({
                    companyName: '',
                    industry: '',
                    industryOther: '',
                    country: '',
                    headcount: '',
                    departments: [],
                    businessFunctions: [],
                    toolsUsed: [],
                    topBottlenecks: ['', '', ''],
                    salaryInput: '',
                    salaryType: 'monthly',
                    aiReadiness: '',
                    budgetRange: '',
                    timelineGoal: '',
                    manualHoursWeekly: '',
                    painPoints: [],
                    orgChartFiles: [],
                    readinessPillars: {
                      data: { data_centralization: null, data_quality: null, data_accessibility: null },
                      technology: { cloud_adoption: null, system_integration: null, security_infrastructure: null },
                      talent: { ai_expertise: null, leadership_buyin: null, change_readiness: null },
                      strategy: { ai_strategy: null, governance_policies: null },
                      resources: { budget_commitment: null, resource_allocation: null }
                    }
                  });
                  setCurrentStep(1);
                  setHasRestoredProgress(false);
                }}
                className="text-xs text-[#00E5C0] hover:text-[#00D4B0] font-medium ml-4"
              >
                Start Fresh
              </button>
            </motion.div>
          )}
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="glass rounded-2xl p-8 mb-8"
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={prevStep}
            disabled={currentStep === 1}
            className="flex items-center gap-2 px-6 py-3 rounded-full glass text-white hover:bg-[rgba(255,255,255,0.1)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={20} />
            Back
          </button>

          {currentStep < totalSteps ? (
            <button
              onClick={nextStep}
              disabled={!isStepValid()}
              data-testid="wizard-next-btn"
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#00E5C0] text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ArrowRight size={20} />
            </button>
          ) : (
            <div className="flex items-center gap-4">
              <span className="text-xs text-[#A0AEC0]" data-testid="credit-cost-estimate-assessment">Estimated cost: ~40 credits</span>
              <button
              onClick={handleSubmit}
              disabled={isProcessing}
              data-testid="wizard-submit-btn"
              className="flex items-center gap-2 px-8 py-3 rounded-full bg-gradient-to-r from-[#14F195] to-[#00E5C0] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {isProcessing ? (
                <>
                  <CircleNotch size={20} className="animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  Start Analysis
                  <ArrowRight size={20} />
                </>
              )}
            </button>
            </div>
          )}
        </div>
      </div>

      {/* Legacy AI Loading Animation removed - Results page has its own progress indicator */}

      {/* Upgrade Modal - Opens in-app, preserves assessment state */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={userPlan || 'basic'}
        message="Upgrade to Unlock Org Chart Analysis"
        feature="Organization Chart AI Analysis"
        showComparison={true}
      />
    </>
  );
};

export default DiagnosticWizard;
